import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { getReportingTimeZone, zonedDayBoundaryIso } from '@/lib/report-timezone';

/** Snap a params object's dateFrom/dateTo to the *business* reporting-zone day
 *  boundaries (midnight→midnight in that zone, converted to precise UTC
 *  instants), regardless of the browser timezone — this is what keeps reports
 *  and exports on the correct local business day instead of GMT. Also attaches
 *  `timeZone` so endpoints that bucket by local day (e.g. total-daily-sales)
 *  can group correctly; endpoints that only filter by instant ignore it.
 *
 *  Pass an explicit `tz` to pin a report to a fixed zone (e.g. a store that
 *  always reports in US Pacific) instead of following the global reporting zone. */
export function withLocalDateRange<T extends { dateFrom?: string; dateTo?: string }>(
  params: T,
  tz?: string,
): Record<string, unknown> {
  const zone = tz ?? getReportingTimeZone();
  return {
    ...params,
    dateFrom: zonedDayBoundaryIso(params.dateFrom, false, zone),
    dateTo: zonedDayBoundaryIso(params.dateTo, true, zone),
    timeZone: zone,
  };
}

export interface PosPayment {
  id: string;
  method: string;
  amount: string;
  status: string;
  cardLastFour: string | null;
  cardBrand: string | null;
  cardType: string | null;
  authCode: string | null;
  terminalRef: string | null;
}

export interface PosTransaction {
  id: string;
  dailyOrderNumber: string | null;
  status: string;
  /** 'payment_check' = auto-held after an unconfirmed card attempt; 'manual' or null otherwise. */
  holdReason?: string | null;
  /** 'card_check' / 'refund_needed' when an earlier card attempt on this order is still open; null otherwise. */
  cardCheck?: string | null;
  subtotal: string;
  tax: string;
  discount: string;
  redemptionDiscount: string | null;
  pointsRedeemed: number | null;
  pointsBalance: number | null;
  total: string;
  customFee1Total: string | null;
  customFee2Total: string | null;
  tipAmount: string | null;
  locationId: string | null;
  locationName: string | null;
  registerId: string | null;
  employeeId: string;
  employeeName: string | null;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  originalTransactionId: string | null;
  createdAt: string;
  createdAtDevice: string | null;
  payments?: PosPayment[];
  // For refunds (which have no payments of their own): the ORIGINAL sale's
  // payments, used only to display Payment Type / Auth Code / Card Last 4.
  originalPayments?: PosPayment[];
}

export interface PosTransactionsParams {
  page?: number;
  perPage?: number;
  status?: string;
  locationId?: string;
  registerId?: string;
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  paymentMethod?: string;
}

export type PaginatedResponse<T> = {
  data: T[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
};

export function usePosTransactions(params: PosTransactionsParams = {}, tz?: string) {
  return useQuery({
    queryKey: ['pos-transactions', params, tz],
    queryFn: () =>
      api.get<PaginatedResponse<PosTransaction>>(
        '/pos/transactions',
        withLocalDateRange(params, tz) as Record<string, string | number | undefined>,
      ),
  });
}

export interface PosCustomer {
  id: string;
  tenantId: string;
  phone: string;
  name: string;
  loyaltyPoints: number;
  totalVisits: number;
  totalSpent: string;
  signupDate: string | null;
  updatedAt: string;
}

export interface PosCustomersParams {
  page?: number;
  perPage?: number;
  search?: string;
  /**
   * 'credit' = customer_type credit/wholesale, 'retail' = everything else.
   * Omitted returns all customers.
   */
  segment?: 'credit' | 'retail';
}

export function usePosCustomers(params: PosCustomersParams = {}) {
  return useQuery({
    queryKey: ['pos-report-customers', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosCustomer>>(
        '/pos/report/customers',
        params as Record<string, string | number | undefined>,
      ),
  });
}

// ── Customer Purchase History ──

export interface PosPurchaseHistoryCustomer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  customerType: string | null;
  businessName: string | null;
}

export interface PosPurchaseHistoryOrder {
  id: string;
  dailyOrderNumber: string | null;
  status: string;
  createdAt: string;
  locationName: string | null;
  employeeName: string | null;
  itemCount: number;
  paymentMethods: string | null;
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
  /** Everything the customer saved on the order (pos.transactions.discount). */
  savings: number;
  /** Parts of `savings` — they add up to it, never on top of it. */
  savingsBreakdown: { promotions: number; rewards: number; coupon: number; couponCode: string | null; manual: number };
}

export interface PosPurchaseHistoryData {
  customer: PosPurchaseHistoryCustomer & { loyaltyPoints: number };
  /** Totals exclude fully refunded orders. */
  summary: { orders: number; totalSpent: number; totalSavings: number };
  orders: PosPurchaseHistoryOrder[];
}

/** Customer picker: matches name, email, phone or business name. */
export function usePosPurchaseHistoryCustomers(search: string) {
  return useQuery({
    queryKey: ['pos-purchase-history-customers', search],
    queryFn: () =>
      api.get<{ data: PosPurchaseHistoryCustomer[] }>('/pos/report/customer-purchase-history/customers', { search }),
    enabled: search.length >= 1,
  });
}

/** One customer's completed orders in a date range. Disabled until a customer is picked. */
export function usePosCustomerPurchaseHistory(
  customerId: string | undefined,
  params: { locationId?: string; dateFrom?: string; dateTo?: string; page?: number; perPage?: number },
  tz?: string,
) {
  return useQuery({
    queryKey: ['pos-customer-purchase-history', customerId, params, tz],
    queryFn: () =>
      api.get<{ data: PosPurchaseHistoryData; pagination: PaginatedResponse<unknown>['pagination'] }>(
        '/pos/report/customer-purchase-history',
        withLocalDateRange({ customerId: customerId as string, ...params }, tz) as Record<
          string,
          string | number | undefined
        >,
      ),
    enabled: !!customerId,
  });
}

// ── Customers by Product ──

export interface PosProductCustomer {
  customerId: string;
  name: string;
  phone: string;
  quantity: number;
  totalSpent: number;
  lastPurchase: string | null;
}

export interface PosCustomersByProductRow {
  id: string;
  name: string;
  primaryBarcode: string | null;
  sku: string | null;
  departmentName: string;
  uniqueCustomers: number;
  quantitySold: number;
  totalRevenue: number;
  customers: PosProductCustomer[];
}

export interface PosCustomersByProductParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export function usePosCustomersByProduct(params: PosCustomersByProductParams = {}) {
  return useQuery({
    queryKey: ['pos-customers-by-product', params],
    queryFn: () =>
      api.get<{
        data: PosCustomersByProductRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/customers-by-product', withLocalDateRange(params) as Record<string, string | number | undefined>),
  });
}

// ── MISC Item / Variable Item ──

interface PosSoldItemBase {
  orderNumber: string;
  transactionId: string;
  locationId: string | null;
  locationName: string;
  orderDate: string;
  quantity: number;
  salePrice: number;
  lineTotal: number;
  employeeName: string;
}

export interface PosExtraItemRow extends PosSoldItemBase {
  itemName: string;
}

export interface PosVariableItemRow extends PosSoldItemBase {
  productName: string;
  departmentName: string;
  description: string;
  tags: string;
}

export interface PosSoldItemParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

type Paged<T> = { data: T[]; pagination: { page: number; perPage: number; total: number; totalPages: number } };

export function usePosExtraItems(params: PosSoldItemParams = {}) {
  return useQuery({
    queryKey: ['pos-extra-items', params],
    queryFn: () =>
      api.get<Paged<PosExtraItemRow>>(
        '/pos/report/extra-items',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

export function usePosVariableItems(params: PosSoldItemParams = {}) {
  return useQuery({
    queryKey: ['pos-variable-items', params],
    queryFn: () =>
      api.get<Paged<PosVariableItemRow>>(
        '/pos/report/variable-items',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Cash In / Cash Out (drawer paid-in / paid-out events) ──

export interface PosCashMovementRow {
  id: string;
  registerId: string | null;
  stationName: string;
  locationId: string | null;
  locationName: string;
  employeeId: string | null;
  employeeName: string;
  amount: number;
  note: string;
  eventTimestamp: string;
}

export interface PosCashMovementParams {
  direction: 'in' | 'out';
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export function usePosCashMovements(params: PosCashMovementParams) {
  return useQuery({
    queryKey: ['pos-cash-movements', params],
    queryFn: () =>
      api.get<{
        data: PosCashMovementRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/cash-movements', withLocalDateRange(params) as Record<string, string | number | undefined>),
  });
}

// ── Vendor by Product ──

export interface PosProductVendor {
  payeeId: string;
  vendorName: string;
  vendorSku: string;
  vendorProductName: string;
  unitName: string;
  costPrice: number | null;
  locationId: string | null;
  locationName: string;
  lastPriceUpdate: string | null;
}

export interface PosVendorByProductData {
  product: { id: string; name: string; primaryBarcode: string | null; sku: string | null };
  vendors: PosProductVendor[];
}

/**
 * Vendors linked to one product. Disabled until a product is selected — the
 * route 400s without a productId. Restricted to owner/admin/manager server-side.
 */
export function usePosVendorByProduct(productId: string | undefined, params: { locationId?: string } = {}) {
  return useQuery({
    queryKey: ['pos-vendor-by-product', productId, params],
    queryFn: () =>
      api.get<{ data: PosVendorByProductData }>('/pos/report/vendor-by-product', {
        productId: productId as string,
        ...params,
      }),
    enabled: !!productId,
  });
}

export interface PosClockEvent {
  id: string;
  employeeId: string;
  employeeName: string | null;
  type: string;
  note: string | null;
  timestampUtc: string;
  locationId: string | null;
  locationName: string | null;
  registerId: string | null;
  registerName: string | null;
  syncedAt: string | null;
}

export interface PosClockEventsParams {
  page?: number;
  perPage?: number;
  type?: string;
  employeeId?: string;
  registerId?: string;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function usePosClockEvents(params: PosClockEventsParams = {}) {
  return useQuery({
    queryKey: ['pos-clock-events', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosClockEvent>>(
        '/pos/report/clock-events',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Employee Work Time (employee-wise, date-wise durations) ──

export interface PosWorkTimeEmployee {
  employeeId: string;
  employeeName: string;
  workedSeconds: number; // net worked (shift time minus breaks) across the range
  breakSeconds: number;
  daysWorked: number;
}

export interface PosWorkTimeParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  registerId?: string;
}

export function usePosWorkTimeEmployees(params: PosWorkTimeParams = {}) {
  return useQuery({
    queryKey: ['pos-work-time-employees', params],
    queryFn: () =>
      api.get<{
        data: PosWorkTimeEmployee[];
        range: { dateFrom: string | null; dateTo: string | null; locationId: string | null; timeZone: string };
      }>('/pos/report/work-time/employees', withLocalDateRange(params) as Record<string, string | undefined>),
  });
}

export interface PosWorkTimePunch {
  type: string;
  timestampUtc: string;
  note: string | null;
  registerName: string | null;
  locationName: string | null;
}

export interface PosWorkTimeDay {
  date: string; // YYYY-MM-DD (business-local)
  workedSeconds: number;
  breakSeconds: number;
  firstClockIn: string | null;
  lastClockOut: string | null;
  openShift: boolean;
  punches: PosWorkTimePunch[];
}

export interface PosWorkTimeDaysParams extends PosWorkTimeParams {
  employeeId?: string;
  page?: number;
  perPage?: number;
  /** Clock event type ('clock_in' | 'clock_out' | 'break_start' | 'break_end').
   *  Omit for all types. Keeps only days with at least one punch of this type;
   *  worked/break totals are unaffected. */
  eventType?: string;
}

export function usePosWorkTimeDays(params: PosWorkTimeDaysParams) {
  return useQuery({
    queryKey: ['pos-work-time-days', params],
    enabled: !!params.employeeId,
    queryFn: () =>
      api.get<PaginatedResponse<PosWorkTimeDay>>(
        '/pos/report/work-time/days',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Coupon / Rewards Usage ──

/** Which POS feature the Coupon/Rewards Usage report is reading. Coupons and
 *  reward programs are separate tables with parallel shapes, so one report
 *  serves both and this picks the pair. */
export type PosCouponUsageKind = 'coupon' | 'reward';

/** A coupon or reward program in the report's left rail, with its usage
 *  roll-up for the selected range. */
export interface PosCouponUsageCoupon {
  id: string;
  code: string; // '' for reward programs — they have no code
  name: string;
  /** coupon: amount_off | percent_off | free_product. reward: points_per_product
   *  | points_per_dollar | points_per_visit. */
  subtype: string;
  /** Coupon discount value, or the reward program's redemption rate. */
  value: number;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  redemptionCount: number;
  uniqueCustomers: number;
  totalDiscount: number;
  lastUsedAt: string | null;
}

/** One redemption of the selected coupon/program: who used it and on what order. */
export interface PosCouponUsageRedemption {
  redemptionId: string;
  customerId: string | null;
  customerName: string;
  customerPhone: string;
  transactionId: string | null;
  orderNumber: string;
  orderDate: string | null;
  orderTotal: number | null;
  discountApplied: number;
  /** Points spent on a reward redemption; null for coupons. */
  pointsDelta: number | null;
  redeemedAt: string;
  locationName: string;
  employeeName: string;
}

export interface PosCouponUsageParams {
  kind?: PosCouponUsageKind;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export function usePosCouponUsageCoupons(params: PosCouponUsageParams = {}) {
  return useQuery({
    queryKey: ['pos-coupon-usage-coupons', params],
    queryFn: () =>
      api.get<{ data: PosCouponUsageCoupon[]; kind: PosCouponUsageKind }>(
        '/pos/report/coupon-usage/coupons',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

export interface PosCouponUsageRedemptionParams extends PosCouponUsageParams {
  /** Coupon id, or reward program id when kind is 'reward'. */
  couponId?: string;
  page?: number;
  perPage?: number;
}

export function usePosCouponUsageRedemptions(params: PosCouponUsageRedemptionParams) {
  return useQuery({
    queryKey: ['pos-coupon-usage-redemptions', params],
    enabled: !!params.couponId,
    queryFn: () =>
      api.get<Paged<PosCouponUsageRedemption>>(
        '/pos/report/coupon-usage/redemptions',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Summary ──

export interface PosReportSummary {
  range: { dateFrom: string | null; dateTo: string | null; locationId: string | null };
  sales: {
    gross: number;
    net: number;
    subtotal: number;
    discount: number;
    tax: number;
    tips: number;
    netAfterRefunds: number;
  };
  /** Gift cards sold on in-range sales (gift card lines, and their amount). Not
   *  a sale: left out of every `sales` figure, while the payments for them stay
   *  in paymentMethods. */
  giftCardsSold: { count: number; amount: number };
  analytics: {
    totalTransactions: number;
    avgPerTransaction: number;
  };
  paymentMethods: { method: string; total: number; count: number; percent: number }[];
  paymentMethodsTotal: number;
  /** Card sales split by brand — `brand` is the raw lowercase code ('visa',
   *  'mastercard', 'amex', 'discover', …) and `count` is transactions, not
   *  tender rows. */
  cardBrands: { brand: string; total: number; count: number; percent: number }[];
  cardBrandsTotal: number;
  refundPaymentMethods: { method: string; total: number; count: number; percent: number }[];
  refundPaymentMethodsTotal: number;
  refunds: {
    fromOrders: { total: number; count: number };
    withoutOrder: { total: number; count: number };
    total: number;
  };
  cashDrawer: {
    byType: { type: string; total: number; count: number }[];
    bankDropTotal: number;
  };
  stationBreakdown: {
    registerId: string | null;
    stationName: string;
    sales: { method: string; total: number }[];
    refunds: { method: string; total: number }[];
    arPayments: { method: string; total: number }[];
    salesTotal: number;
    refundsTotal: number;
    tips: number;
  }[];
  storeCreditSummary: {
    issued: number;
    redeemed: number;
    openingLiability: number;
    endingLiability: number;
  };
  // Gift Card liability — same shape/ledger as store credit, split by payment_type.
  giftCardSummary: {
    issued: number;
    redeemed: number;
    openingLiability: number;
    endingLiability: number;
  };
  creditAccountSummary: {
    creditAccountSales: number;
    paymentsReceived: number;
    openingARBalance: number;
    endingARBalance: number;
    // A/R settlement payments in the range, split by the tender used to collect
    // them. Feeds the "A / R (Accounts Receivable)" row of the Sales & Refunds
    // matrix; sums to paymentsReceived.
    paymentsByMethod: { method: string; total: number }[];
  };
}

export interface PosReportSummaryParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
}

export function usePosReportSummary(params: PosReportSummaryParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-report-summary', params],
    queryFn: () =>
      api.get<{ data: PosReportSummary }>(
        '/pos/report/summary',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

// ── Payments Per Station ──

export interface PosPaymentsPerStationRow {
  registerId: string | null;
  stationName: string;
  methods: {
    card: number;
    /** Credit Account (house account) charges — POS method 'credit'. */
    creditAccount: number;
    cash: number;
    ebt: number;
    giftCardOrStoreCredit: number;
    check: number;
    other: number;
    tips: number;
  };
  netTotal: number;
}

export interface PosPaymentsPerStationParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
}

export function usePosPaymentsPerStation(params: PosPaymentsPerStationParams = {}) {
  return useQuery({
    queryKey: ['pos-payments-per-station', params],
    queryFn: () =>
      api.get<{
        data: PosPaymentsPerStationRow[];
        range: { dateFrom: string | null; dateTo: string | null; locationId: string | null };
      }>('/pos/report/payments-per-station', withLocalDateRange(params) as Record<string, string | undefined>),
  });
}

// ── Total Daily Sales ──

export interface PosTotalDailySalesRow {
  date: string;
  ordersCount: number;
  sales: number;
  tax: number;
  fees: number;
  discounts: number;
  tips: number;
  /** Gift cards sold that day: out of `sales`, still in `payments`. */
  giftCards: number;
  payments: number;
  refundedAmount: number;
}

export interface PosTotalDailySalesTotals {
  totalBilledAmount: number;
  totalTransactions: number;
  totalSales: number;
  totalTax: number;
  totalFees: number;
  totalTip: number;
  totalDiscount: number;
  totalCashDiscount: number;
  totalGiftCards: number;
  totalRefundedAmountWithOrder: number;
  totalRefundedTaxWithOrder: number;
  totalRefundedFeesWithOrder: number;
  totalRefundedTips: number;
  totalRefundedAmountWithoutOrder: number;
  totalRefundedTaxWithoutOrder: number;
  totalRefundedFeesWithoutOrder: number;
}

export interface PosTotalDailySales {
  range: { dateFrom: string | null; dateTo: string | null; locationId: string | null };
  totals: PosTotalDailySalesTotals;
  daily: PosTotalDailySalesRow[];
}

export interface PosTotalDailySalesParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  /** `hour` buckets each row by the hour (`date` becomes `YYYY-MM-DDTHH:00`)
   *  instead of the calendar day. Omitted, the server keeps its per-day rows. */
  granularity?: 'day' | 'hour';
}

export function usePosTotalDailySales(params: PosTotalDailySalesParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-total-daily-sales', params],
    queryFn: () =>
      api.get<{ data: PosTotalDailySales }>(
        '/pos/report/total-daily-sales',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

// ── New vs returning customers (home page card) ──

export interface PosCustomerMix {
  range: { dateFrom: string | null; dateTo: string | null; locationId: string | null };
  /** Customers whose first sale at this location falls inside the range. */
  newCustomers: number;
  /** Customers in the range who also bought before it started. */
  returningCustomers: number;
  totalCustomers: number;
  /** Sales with no customer attached — excluded from both counts above. */
  anonymousSales: number;
}

export function usePosNewVsReturningCustomers(
  params: { dateFrom?: string; dateTo?: string; locationId?: string } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-new-vs-returning-customers', params],
    queryFn: () =>
      api.get<{ data: PosCustomerMix }>(
        '/pos/report/new-vs-returning-customers',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

// ── Departments (for filter dropdown) ──

export interface PosReportDepartment {
  id: string;
  name: string;
}

export function usePosReportDepartments() {
  return useQuery({
    queryKey: ['pos-report-departments'],
    queryFn: () => api.get<{ data: PosReportDepartment[] }>('/pos/report/departments'),
  });
}

// ── Total Daily Department Sales ──

/** Actual sales in scope, each sale line counted once. */
export interface PosSalesGroupTotals {
  quantitySold: number;
  grossSale: number;
  costOfSale: number;
  quantityWithoutCost: number;
}

export interface PosDepartmentSalesRow {
  departmentId: string | null;
  departmentName: string;
  quantitySold: number;
  freeQuantity: number;
  netQuantitySold: number;
  grossSale: number;
  percentage: number;
  costOfSale: number;
  /** Units whose product has no cost at the sale's location (counted as $0 cost). */
  quantityWithoutCost: number;
}

export interface PosTotalDailyDepartmentSalesParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  departmentIds?: string;
}

export function usePosTotalDailyDepartmentSales(
  params: PosTotalDailyDepartmentSalesParams = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-total-daily-department-sales', params],
    queryFn: () =>
      api.get<{
        data: PosDepartmentSalesRow[];
        totals: PosSalesGroupTotals;
        range: {
          dateFrom: string | null;
          dateTo: string | null;
          locationId: string | null;
          departmentIds: string[] | null;
        };
      }>('/pos/report/total-daily-department-sales', withLocalDateRange(params) as Record<string, string | undefined>),
  });
}

// ── Tags (for the Sales By Vendor filter — kept for that report only) ──

export interface PosReportTag {
  id: string;
  name: string;
}

export function usePosReportTags() {
  return useQuery({
    queryKey: ['pos-report-tags'],
    queryFn: () => api.get<{ data: PosReportTag[] }>('/pos/report/tags'),
  });
}

// ── Categories (for filter dropdown on Total Daily Categories Sale) ──

export interface PosReportCategory {
  id: string;
  name: string;
}

export function usePosReportCategories() {
  return useQuery({
    queryKey: ['pos-report-categories'],
    queryFn: () => api.get<{ data: PosReportCategory[] }>('/pos/report/categories'),
  });
}

// ── Total Daily Category Sales ──

export interface PosCategorySalesRow {
  categoryId: string | null;
  categoryName: string;
  quantitySold: number;
  freeQuantity: number;
  netQuantitySold: number;
  grossSale: number;
  percentage: number;
  costOfSale: number;
  /** Units whose product has no cost at the sale's location (counted as $0 cost). */
  quantityWithoutCost: number;
}

export interface PosTotalDailyCategorySalesParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  categoryIds?: string;
}

// ── Tax Report ──

export interface PosTaxReportBreakdown {
  paymentAmountCollected: number; // A
  tipAmountCollected: number; // B
  totalPaymentCollected: number; // C = A + B
  paymentRefundedWithOrder: number; // D
  paymentRefundedWithoutOrder: number; // E
  paymentCollected: number; // F = C - D - E
  unpaidAmount: number; // G
  taxAmountCollected: number; // I
  taxRefundedWithOrder: number; // J
  taxRefundedWithoutOrder: number; // K
  netTaxCollected: number; // L = I - J - K
  feesAmountCollected: number; // M
  feesRefundedWithOrder: number; // N
  feesRefundedWithoutOrder: number; // O
  netFeeCollected: number; // P = M - N - O
  netPaymentCollected: number; // Q = F - J - K - N - O
}

export interface PosTaxDetailRow {
  taxId: number;
  taxName: string;
  taxEligibleAmount: number;
  taxCollected: number;
}

export interface PosFeeDetailRow {
  feeId: number;
  feeName: string;
  feeEligibleAmount: number;
  feeCollected: number;
}

export interface PosTaxReport {
  range: { dateFrom: string | null; dateTo: string | null; locationId: string | null };
  sales: { taxableSales: number; nonTaxableSales: number; netSales: number };
  breakdown: PosTaxReportBreakdown;
  taxDetails: PosTaxDetailRow[];
  feeDetails: PosFeeDetailRow[];
}

export interface PosTaxReportParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
}

export function usePosTaxReport(params: PosTaxReportParams = {}) {
  return useQuery({
    queryKey: ['pos-tax-report', params],
    queryFn: () =>
      api.get<{ data: PosTaxReport }>(
        '/pos/report/tax-report',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

// ── Top Items by Sales & Volume ──

export interface PosTopItemRow {
  productId: string;
  productName: string;
  totalSalesAmount: number;
  unitsSold: number;
}

export interface PosTopItemsParams {
  page?: number;
  perPage?: number;
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  search?: string;
}

export function usePosTopItems(params: PosTopItemsParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-top-items', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosTopItemRow>>(
        '/pos/report/top-items',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Single Order Detail (for All Orders modal) ──

export interface PosOrderItem {
  id: string;
  productId: string;
  productName: string;
  primaryBarcode: string | null;
  unitName: string;
  qty: number;
  weightKg: number | null;
  unitPrice: number;
  lineTotal: number;
  tax: number;
  discount: number;
  /** % a plain line discount was entered as (null for flat/promo lines). */
  discountPercent: number | null;
  appliedPromotionId: string | null;
  appliedPromotionName: string | null;
  ebtEligible: boolean;
  note: string | null;
}

export interface PosOrderPayment {
  id: string;
  method: string;
  amount: string;
  status: string;
  terminalRef: string | null;
  authCode: string | null;
  cardLastFour: string | null;
  cardBrand: string | null;
  cardType: string | null;
  responseCode: string | null;
}

export interface PosOrderRefund {
  id: string;
  dailyOrderNumber: string | number | null;
  status: string;
  total: number;
  tax: number;
  /** Refund reason chosen on the POS, stored on the refund transaction's
   *  `note` column — shown as "Reason" in the order-details Refund tab. */
  note: string | null;
  createdAt: string;
}

export interface PosOrderDetail {
  id: string;
  dailyOrderNumber: string | number | null;
  status: string;
  holdReason?: string | null;
  cardCheck?: string | null;
  subtotal: string;
  tax: string;
  discount: string;
  redemptionDiscount: string | null;
  pointsRedeemed: number | null;
  pointsBalance: number | null;
  total: string;
  customFee1Total: string;
  customFee1Label: string;
  customFee2Total: string;
  customFee2Label: string;
  tipAmount: string;
  promotionApplied: boolean;
  note: string | null;
  locationId: string | null;
  locationName: string | null;
  locationAddress: string | null;
  storeName: string | null;
  storePhone: string | null;
  storeAddress: string | null;
  storeCity: string | null;
  storeState: string | null;
  receiptHeader: string | null;
  receiptFooter: string | null;
  registerId: string | null;
  stationName: string | null;
  employeeId: string;
  employeeName: string | null;
  createdAt: string;
  createdAtDevice: string | null;
  originalTransactionId: string | null;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customerLoyaltyPoints: number | null;
  items: PosOrderItem[];
  payments: PosOrderPayment[];
  refunds: PosOrderRefund[];
}

export function usePosOrderDetail(id: string | null) {
  return useQuery({
    queryKey: ['pos-order-detail', id],
    enabled: !!id,
    queryFn: () => api.get<{ data: PosOrderDetail }>(`/pos/transactions/${id}`),
  });
}

// ── Sales By Vendor ──

export interface PosSalesByVendorVendor {
  vendorId: string;
  vendorName: string;
  totalUnitsSold: number;
}

export interface PosSalesByVendorProduct {
  productId: string;
  productName: string;
  primaryBarcode: string | null;
  departmentName: string;
  inStockQty: number;
  unitsSold: number;
  totalSales: number;
}

export interface PosSalesByVendorVendorsParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  vendorIds?: string;
  departmentIds?: string;
  tagIds?: string;
}

export function usePosSalesByVendorVendors(params: PosSalesByVendorVendorsParams = {}) {
  return useQuery({
    queryKey: ['pos-sales-by-vendor-vendors', params],
    queryFn: () =>
      api.get<{ data: PosSalesByVendorVendor[] }>(
        '/pos/report/sales-by-vendor/vendors',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
  });
}

export interface PosSalesByVendorProductsParams extends PosSalesByVendorVendorsParams {
  vendorId?: string;
  search?: string;
  page?: number;
  perPage?: number;
}

export function usePosSalesByVendorProducts(params: PosSalesByVendorProductsParams) {
  return useQuery({
    queryKey: ['pos-sales-by-vendor-products', params],
    enabled: !!params.vendorId,
    queryFn: () =>
      api.get<PaginatedResponse<PosSalesByVendorProduct>>(
        '/pos/report/sales-by-vendor/products',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

export interface PosReportVendor {
  id: string;
  name: string;
}

export function usePosReportVendors() {
  return useQuery({
    queryKey: ['pos-report-sp-payees'],
    queryFn: () => api.get<{ data: PosReportVendor[] }>('/pos/report/sp-payees'),
  });
}

export function usePosTotalDailyCategorySales(
  params: PosTotalDailyCategorySalesParams = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-total-daily-category-sales', params],
    queryFn: () =>
      api.get<{
        data: PosCategorySalesRow[];
        totals: PosSalesGroupTotals;
        range: {
          dateFrom: string | null;
          dateTo: string | null;
          locationId: string | null;
          categoryIds: string[] | null;
        };
      }>('/pos/report/total-daily-category-sales', withLocalDateRange(params) as Record<string, string | undefined>),
  });
}

// ── Inventory Movement & Profitability ──

export type PosInventorySortBy =
  | 'name'
  | 'primaryBarcode'
  | 'inStockQty'
  | 'quantitySold'
  | 'cashPrice'
  | 'cost'
  | 'totalRevenue';

export interface PosInventoryMovementRow {
  id: string;
  name: string;
  primaryBarcode: string | null;
  sku: string | null;
  departmentName: string;
  categoryNames: string;
  inStockQty: number;
  cost: number | null;
  cashPrice: number | null;
  quantitySold: number;
  totalCost: number;
  totalDiscount: number;
  totalRevenue: number;
}

export interface PosInventoryMovementParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  categoryIds?: string;
  departmentIds?: string;
  search?: string;
  sortBy?: PosInventorySortBy;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export function usePosInventoryMovement(params: PosInventoryMovementParams = {}) {
  return useQuery({
    queryKey: ['pos-inventory-movement', params],
    queryFn: () =>
      api.get<{
        data: PosInventoryMovementRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>(
        '/pos/report/inventory-movement-profitability',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Daily Item Movements ──

export interface PosDailyItemMovementRow {
  locationId: string | null;
  locationName: string;
  productId: string;
  productName: string;
  sku: string | null;
  barcode: string | null;
  departmentName: string;
  categoryNames: string;
  qtySold: number;
  /** Pounds; null when nothing sold by weight. */
  weightSold: number | null;
  grossSales: number;
  discounts: number;
  returns: number;
  netSales: number;
  cogs: number;
}

export interface PosDailyItemMovementParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  page?: number;
  perPage?: number;
}

export function usePosDailyItemMovements(params: PosDailyItemMovementParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['pos-daily-item-movements', params],
    queryFn: () =>
      api.get<{
        data: PosDailyItemMovementRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/daily-item-movements', withLocalDateRange(params) as Record<string, string | number | undefined>),
    enabled: options.enabled ?? true,
  });
}

// ── Inventory Valuation & Summary ──

export type PosInventoryValuationSortBy = 'name' | 'primaryBarcode' | 'inStockQty';

export interface PosInventoryValuationRow {
  id: string;
  name: string;
  primaryBarcode: string | null;
  sku: string | null;
  departmentName: string;
  categoryNames: string;
  inStockQty: number;
  cost: number | null;
  sellingPrice: number | null;
  retailValuation: number;
  costValuation: number;
  locationName: string | null;
}

export interface PosInventoryValuationParams {
  locationId?: string;
  categoryIds?: string;
  departmentIds?: string;
  vendorIds?: string;
  search?: string;
  sortBy?: PosInventoryValuationSortBy;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export function usePosInventoryValuation(params: PosInventoryValuationParams = {}) {
  return useQuery({
    queryKey: ['pos-inventory-valuation', params],
    queryFn: () =>
      api.get<{
        data: PosInventoryValuationRow[];
        totals: { retail: number; cost: number };
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/inventory-valuation', params as Record<string, string | number | undefined>),
  });
}

// ── Price Updates ──

export type PosPriceUpdatesSortBy = 'name' | 'primaryBarcode' | 'sellingPrice' | 'rewardPoints';

export interface PosPriceUpdateRow {
  id: string;
  name: string;
  primaryBarcode: string | null;
  sku: string | null;
  sellingPrice: number | null;
  previousPrice: number | null;
  rewardPoints: number;
  departmentName: string;
  categoryNames: string;
  inStockQty: number;
  lastChangedAt: string | null;
}

export interface PosPriceUpdatesParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  categoryIds?: string;
  departmentIds?: string;
  sortBy?: PosPriceUpdatesSortBy;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export function usePosPriceUpdates(params: PosPriceUpdatesParams = {}) {
  return useQuery({
    queryKey: ['pos-price-updates', params],
    queryFn: () =>
      api.get<{
        data: PosPriceUpdateRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/price-updates', withLocalDateRange(params) as Record<string, string | number | undefined>),
  });
}

// ── POS Price Changes (line overrides + catalog updates made on the POS) ──

export interface PosPriceChangeRow {
  id: string;
  productName: string;
  oldPrice: number;
  newPrice: number;
  changedBy: string;
  registerName: string | null;
  source: string; // 'order_override' | 'product_update'
  transactionId: string | null;
  orderNumber: string | null;
  createdAt: string;
}

export interface PosPriceChangesParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  source?: string;
}

export function usePosPriceChanges(params: PosPriceChangesParams = {}) {
  return useQuery({
    queryKey: ['pos-price-changes', params],
    queryFn: () =>
      api.get<{ data: PosPriceChangeRow[] }>(
        '/pos/report/price-changes',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Card attempts to check (unverified card attempts / duplicates to refund) ──

export interface PosCardAttemptRow {
  id: string;
  /** duplicate | unverified | unverified_expired | resolved */
  status: string;
  amount: number;
  approvedAmount: number | null;
  terminalRef: string | null;
  authCode: string | null;
  cardBrand: string | null;
  registerId: string | null;
  registerName: string | null;
  paidOrderId: string | null;
  paidOrderNumber: string | null;
  startedAt: string | null;
  updatedAt: string;
  refundedAt: string | null;
  refundNote: string | null;
}

export interface PosCardAttemptsParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  /** refund_needed | refunded | unverified | unverified_expired | resolved | duplicate */
  status?: string;
}

export function usePosCardAttempts(params: PosCardAttemptsParams = {}) {
  return useQuery({
    queryKey: ['pos-card-attempts', params],
    queryFn: () =>
      api.get<{ data: PosCardAttemptRow[] }>(
        '/pos/report/card-attempts',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

export function useMarkCardAttemptRefunded() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<{ ok: boolean }>(`/pos/report/card-attempts/${encodeURIComponent(id)}/refunded`, { note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pos-card-attempts'] }),
  });
}

// ── Inventory Trail ──

export type PosInventoryTrailSortBy = 'name' | 'department' | 'type' | 'quantity' | 'date' | 'user';

export interface PosInventoryTrailRow {
  id: string;
  productId: string;
  name: string;
  primaryBarcode: string | null;
  sku: string | null;
  departmentName: string;
  categoryNames: string;
  type: string;
  quantity: number;
  userName: string;
  createdAt: string;
  locationName: string;
  description: string;
}

export interface PosInventoryTrailParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  categoryIds?: string;
  departmentIds?: string;
  operations?: string;
  search?: string;
  sortBy?: PosInventoryTrailSortBy;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export function usePosInventoryTrail(params: PosInventoryTrailParams = {}) {
  return useQuery({
    queryKey: ['pos-inventory-trail', params],
    queryFn: () =>
      api.get<{
        data: PosInventoryTrailRow[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/pos/report/inventory-trail', withLocalDateRange(params) as Record<string, string | number | undefined>),
  });
}

// ── POS Stations (for Refunds filter) ──

export interface PosStation {
  registerId: string;
  stationName: string;
}

export interface PosStationsParams {
  locationId?: string;
}

export function usePosStations(params: PosStationsParams = {}) {
  return useQuery({
    queryKey: ['pos-stations', params],
    queryFn: () =>
      api.get<{ data: PosStation[] }>('/pos/report/stations', params as Record<string, string | undefined>),
  });
}

// ── Refunds ──

export type PosRefundsTab = 'with-order' | 'without-order';

export interface PosRefundPayment {
  method: string;
  amount: string;
  authCode: string | null;
  terminalRef: string | null;
}

export interface PosRefundRow {
  id: string;
  dailyOrderNumber: string | null;
  originalTransactionId: string | null;
  originalDailyOrderNumber: string | null;
  originalTotal: number | null;
  refundAmount: number;
  refundTaxFees: number;
  refundTotal: number;
  refundTips: number;
  locationId: string | null;
  locationName: string | null;
  registerId: string | null;
  stationName: string;
  employeeId: string;
  employeeName: string | null;
  createdAt: string;
  payments: PosRefundPayment[];
}

export interface PosRefundsTotals {
  refundAmount: number;
  refundTaxFees: number;
  refundTotal: number;
  refundTips: number;
}

/** Range total for one tender. `method` is 'none' for refunds with no tender
 *  recorded; the rows add up to totals.refundTotal. */
export interface PosRefundsMethodTotal {
  method: string;
  total: number;
  count: number;
}

export interface PosRefundsResponse {
  data: PosRefundRow[];
  totals: PosRefundsTotals;
  /** Absent from an API that predates the by-method breakdown. */
  byMethod?: PosRefundsMethodTotal[];
  range: {
    dateFrom: string | null;
    dateTo: string | null;
    locationId: string | null;
    registerIds: string[] | null;
    tab: PosRefundsTab;
  };
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

export interface PosRefundsParams {
  tab: PosRefundsTab;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  registerIds?: string;
  page?: number;
  perPage?: number;
  sortDir?: 'asc' | 'desc';
}

export function usePosRefunds(params: PosRefundsParams, tz?: string) {
  return useQuery({
    queryKey: ['pos-refunds', params, tz],
    queryFn: () =>
      api.get<PosRefundsResponse>(
        '/pos/report/refunds',
        withLocalDateRange(params, tz) as unknown as Record<string, string | number | undefined>,
      ),
  });
}

// ── Store Credits ──

export interface PosStoreCreditRow {
  id: string;
  code: string;
  initialAmount: number;
  balance: number;
  paymentType: string;
  createdByEmployeeId: string | null;
  createdByName: string | null;
  locationId: string | null;
  locationName: string | null;
  createdAt: string;
}

export interface PosStoreCreditsParams {
  page?: number;
  perPage?: number;
  search?: string;
  locationId?: string;
}

export function usePosStoreCredits(params: PosStoreCreditsParams = {}) {
  return useQuery({
    queryKey: ['pos-store-credits', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosStoreCreditRow>>(
        '/pos/report/store-credits',
        params as Record<string, string | number | undefined>,
      ),
  });
}

export interface PosStoreCreditAuditRow {
  id: string;
  description: string;
  amount: number;
  type: string;
  balance: number;
  employeeId: string | null;
  employeeName: string | null;
  orderId: string | null;
  orderNumber: string | null;
  occurredAt: string;
}

export interface PosStoreCreditDetailResponse {
  data: PosStoreCreditRow;
  audit: PosStoreCreditAuditRow[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

export interface PosStoreCreditDetailParams {
  page?: number;
  perPage?: number;
}

// ── Employees (for Sales by Cashier filter) ──

export interface PosReportEmployee {
  id: string;
  name: string;
}

export function usePosReportEmployees() {
  return useQuery({
    queryKey: ['pos-report-employees'],
    queryFn: () => api.get<{ data: PosReportEmployee[] }>('/pos/report/employees'),
  });
}

// ── Sales by Cashier ──

export interface PosSalesByCashierCashier {
  employeeId: string;
  employeeName: string;
  ordersCount: number;
  totalSales: number;
}

export interface PosSalesByCashierCashiersParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
  employeeIds?: string;
  departmentIds?: string;
  categoryIds?: string;
}

export function usePosSalesByCashierCashiers(params: PosSalesByCashierCashiersParams = {}) {
  return useQuery({
    queryKey: ['pos-sales-by-cashier-cashiers', params],
    queryFn: () =>
      api.get<{
        data: PosSalesByCashierCashier[];
        range: {
          dateFrom: string | null;
          dateTo: string | null;
          locationId: string | null;
          employeeIds: string[] | null;
          departmentIds: string[] | null;
          categoryIds: string[] | null;
        };
      }>('/pos/report/sales-by-cashier/cashiers', withLocalDateRange(params) as Record<string, string | undefined>),
  });
}

export interface PosSalesByCashierOrder {
  id: string;
  dailyOrderNumber: string | null;
  total: number;
  registerId: string | null;
  stationName: string;
  locationId: string | null;
  locationName: string | null;
  createdAt: string;
}

export interface PosSalesByCashierOrdersParams extends PosSalesByCashierCashiersParams {
  employeeId?: string;
  page?: number;
  perPage?: number;
}

export function usePosSalesByCashierOrders(params: PosSalesByCashierOrdersParams) {
  return useQuery({
    queryKey: ['pos-sales-by-cashier-orders', params],
    enabled: !!params.employeeId,
    queryFn: () =>
      api.get<PaginatedResponse<PosSalesByCashierOrder>>(
        '/pos/report/sales-by-cashier/orders',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Discount Report ──

export type PosDiscountType = 'order' | 'item' | 'both';
export type PosDiscountSortBy =
  | 'id'
  | 'orderId'
  | 'location'
  | 'orderDate'
  | 'orderDiscount'
  | 'itemDiscount'
  | 'employeeName'
  | 'orderDiscountReason';

export interface PosDiscountRow {
  id: string;
  dailyOrderNumber: string | null;
  locationId: string | null;
  locationName: string | null;
  employeeId: string | null;
  employeeName: string | null;
  orderDiscount: number;
  itemDiscount: number;
  /** Discounted item name(s) for an item-level discount; null for order-level. */
  itemNames: string | null;
  discountType: PosDiscountType;
  predefined: boolean;
  orderDiscountReason: string | null;
  createdAt: string;
}

export interface PosDiscountReportParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  discountType?: PosDiscountType;
  sortBy?: PosDiscountSortBy;
  sortDir?: 'asc' | 'desc';
}

export function usePosDiscountReport(params: PosDiscountReportParams = {}) {
  return useQuery({
    queryKey: ['pos-discount-report', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosDiscountRow>>(
        '/pos/report/discounts',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Digital Transactions Report ──

export type PosDigitalTxnType = 'sale' | 'refund';
export type PosDigitalTxnSortBy = 'orderId' | 'amount' | 'orderDate' | 'location' | 'cardEnding' | 'authCode';

export interface PosDigitalTransactionRow {
  id: string;
  transactionId: string;
  dailyOrderNumber: string | null;
  transactionType: PosDigitalTxnType;
  amount: number;
  cardLastFour: string | null;
  cardBrand: string | null;
  authCode: string | null;
  terminalRef: string | null;
  locationId: string | null;
  locationName: string | null;
  registerId: string | null;
  stationName: string;
  createdAt: string;
}

export interface PosDigitalTransactionsTotals {
  salesTotal: number;
  refundsTotal: number;
  netTotal: number;
}

export interface PosDigitalTransactionsResponse {
  data: PosDigitalTransactionRow[];
  totals: PosDigitalTransactionsTotals;
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

export interface PosDigitalTransactionsParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  registerIds?: string;
  transactionType?: PosDigitalTxnType;
  search?: string;
  sortBy?: PosDigitalTxnSortBy;
  sortDir?: 'asc' | 'desc';
}

export function usePosDigitalTransactions(params: PosDigitalTransactionsParams = {}) {
  return useQuery({
    queryKey: ['pos-digital-transactions', params],
    queryFn: () =>
      api.get<PosDigitalTransactionsResponse>(
        '/pos/report/digital-transactions',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

export function usePosStoreCreditDetail(id: string | null, params: PosStoreCreditDetailParams = {}) {
  return useQuery({
    queryKey: ['pos-store-credit-detail', id, params],
    enabled: !!id,
    queryFn: () =>
      api.get<PosStoreCreditDetailResponse>(
        `/pos/report/store-credits/${id}`,
        params as Record<string, string | number | undefined>,
      ),
  });
}

// ── Promotion Usage Report ──

export type PosPromotionSortBy = 'name' | 'totalUses' | 'totalDiscount' | 'grossSales' | 'lastUsedAt';

export interface PosPromotionUsageRow {
  promotionId: string;
  promotionName: string | null;
  totalUses: number;
  totalOrders: number;
  totalDiscount: number;
  grossSales: number;
  firstUsedAt: string | null;
  lastUsedAt: string | null;
}

export interface PosPromotionReportParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sortBy?: PosPromotionSortBy;
  sortDir?: 'asc' | 'desc';
}

export function usePosPromotionReport(params: PosPromotionReportParams = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['pos-promotion-report', params],
    queryFn: () =>
      api.get<PaginatedResponse<PosPromotionUsageRow>>(
        '/pos/report/promotions',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Customer detail history (purchases + point earn/redeem events) ──────────
export interface CustomerHistoryPurchase {
  id: string;
  dailyOrderNumber: string | null;
  total: string;
  tipAmount: string | null;
  status: string;
  createdAt: string;
}

export interface CustomerHistoryReward {
  id: string;
  eventType: 'earn' | 'redeem';
  pointsDelta: number;
  discountApplied: string | null;
  createdAt: string;
}

export function useCustomerHistory(customerId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['pos-customer-history', customerId],
    queryFn: () =>
      api.get<{ data: { purchases: CustomerHistoryPurchase[]; rewards: CustomerHistoryReward[] } }>(
        `/pos/customers/${customerId}/history`,
      ),
    enabled: (options.enabled ?? true) && !!customerId,
    staleTime: 15_000,
  });
}

// ── Tills report (Reports → POS → Tills) ────────────────────────────────────
// Till (cash-drawer) sessions. Backed by GET /pos/report/tills. NOTE: the
// backend is currently a scaffold that returns an empty result set until a
// till-session data model is synced from the POS — the page still renders,
// filters, sorts and paginates exactly like the other reports.
export type PosTillStatus = 'active' | 'inactive' | 'open' | 'closed';
export type PosTillSortBy =
  | 'id'
  | 'posStation'
  | 'startingAmount'
  | 'closingAmount'
  | 'bankDropAmount'
  | 'status'
  | 'difference'
  | 'createdAt';

export interface PosTillRow {
  id: string;
  // Human-facing sequential till id (e.g. 8845); null until the model exists.
  tillNumber: number | null;
  registerId: string | null;
  stationName: string | null;
  locationId: string | null;
  locationName: string | null;
  startingAmount: number;
  startingEmployeeName: string | null;
  closingAmount: number | null;
  closingEmployeeName: string | null;
  bankDropAmount: number;
  status: PosTillStatus;
  difference: number;
  createdAt: string;
  // Denomination breakdown captured on the POS (open/close), JSON array of
  // { label, value, count }. Null when the cashier didn't count denominations.
  startingDenominations?: PosTillDenomination[] | null;
  closingDenominations?: PosTillDenomination[] | null;
}

export interface PosTillDenomination {
  label: string;
  value: number;
  count: number;
}

export interface PosTillsParams {
  page?: number;
  perPage?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  status?: 'active' | 'inactive';
  sortBy?: PosTillSortBy;
  sortDir?: 'asc' | 'desc';
}

export interface PosTillsResponse {
  data: PosTillRow[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}

export function usePosTills(params: PosTillsParams = {}) {
  return useQuery({
    queryKey: ['pos-tills', params],
    queryFn: () =>
      api.get<PosTillsResponse>(
        '/pos/report/tills',
        withLocalDateRange(params) as Record<string, string | number | undefined>,
      ),
  });
}

// ── Cash reconciliation (Reconciliation → Cash) ─────────────────────────────
export interface PosCashReconciliationRow {
  date: string; // YYYY-MM-DD (business-local day)
  registerId: string | null;
  registerName: string;
  employeeId: string | null;
  employeeName: string | null;
  systemReported: number; // POS cash for that day+register
  actualReported: number | null; // user-entered, null until saved
  bookKeepingNotes: string | null;
  // Who last saved the count, and when (ISO). Null until saved. Optional so an
  // API from before these fields still type-checks.
  countedByUserId?: string | null;
  countedByName?: string | null;
  countUpdatedAt?: string | null;
}

export interface PosCashReconciliationsParams {
  dateFrom?: string;
  dateTo?: string;
  locationId?: string;
}

export function usePosCashReconciliations(params: PosCashReconciliationsParams = {}) {
  return useQuery({
    queryKey: ['pos-cash-reconciliations', params],
    queryFn: () =>
      api.get<{ data: PosCashReconciliationRow[] }>(
        '/pos/report/cash-reconciliations',
        withLocalDateRange(params) as Record<string, string | undefined>,
      ),
    enabled: !!params.locationId,
  });
}

export interface SaveCashReconciliationInput {
  locationId: string;
  date: string; // YYYY-MM-DD
  registerId: string;
  registerName?: string | null;
  employeeId?: string | null;
  employeeName?: string | null;
  systemReported: number;
  actualReported: number;
  bookKeepingNotes?: string | null;
}

export function useSaveCashReconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveCashReconciliationInput) =>
      api.put<{ data: unknown }>('/pos/report/cash-reconciliations', input),
    onSuccess: () => {
      // Keep the cache warm for the next mount without disrupting in-progress
      // inline edits (the panel overlays local edits on top of server data).
      queryClient.invalidateQueries({ queryKey: ['pos-cash-reconciliations'], refetchType: 'none' });
    },
  });
}
